import React, { useEffect } from 'react'
import { AppProps } from '../../App'
import { useNavigate } from 'react-router-dom';
import { getRandomString, useEventListener } from '../../utils/functions';
import { Input } from '../../components/Input';
import Loading from '../../components/Loading';
import { UserModel } from '../../utils/interfaces';
import { storageKeys, tables } from '../../utils/constants';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/Navbar';
import { getItem, createItem, userConfirmSignUp, userSignUp } from '../../api/API';
import Footer from '../../components/FooterLanding';

const SignUp = (props: AppProps) => {

  const { t } = useTranslation();
  const navigate = useNavigate();
  const submitRef = React.useRef<HTMLButtonElement>(null);
  const [name, setName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  const [verificationCode, setVerificationCode] = React.useState('');
  const [errorMessage, setErrorMessage] = React.useState('');
  const [isCodeSent, setIsCodeSent] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);

  useEffect(() => {
    if (props.user) {
      navigate('/');
    }
  }, [props.user, navigate]);

  useEventListener('keydown', (e) => {
    if (e.key === 'Enter') {  
      submitRef.current?.click();
    }
  })

  // sign up
  const submit = async () => {
    setIsLoading(true);
    setErrorMessage('');
    if (isCodeSent) { // confirm sign up
      const response = await userConfirmSignUp(email.replace('@', '-at-'), verificationCode);
      if (response.error) {
        setErrorMessage(t('auth.sign-up-error.incorrect-code'));
      } else {
        let user: UserModel | undefined;
        try {
          // setup user when successfully signed up
          const data = await getItem(tables.users, email);
          if (!data.item.error) {
            user = { ...data.item, name: name };
          } else {
            user = { id: email, id2: getRandomString(10), name, email, plan: { name: 'free' } };
          }
        } catch (error) { }
        await createItem(tables.users, user);
        // finished sign up
        props.setUser(user);
        localStorage.setItem(storageKeys.userId, email);
        localStorage.setItem(storageKeys.userId2, user?.id2 || '');
        props.setIsAIOpened(true);
      }
    } else { // sign up and send verification code
      const response = await userSignUp(email.replace('@', '-at-'), password, email);
      if (response.error) {
        setErrorMessage(t('auth.sign-up-error.email-exists'));
      } else {
        setIsCodeSent(true);
      }
    }
    setIsLoading(false);
  }

  return (
    <>
      {props.user !== undefined && <Navbar {...props} />}
      <main className='my-20 flex items-center justify-center p-8'>
        <div className='form w-full max-w-xs'>
          <div className='text-2xl md:text-3xl'>{t('auth.sign-up')}</div>
          {errorMessage && <div className='text-xs md:text-sm text-red-400 mt-4'>{errorMessage}</div>}
          <div className='mt-4'>
            <label className='text-xs md:text-sm text-black/50 m-1'>Email</label>
            <Input autoFocus value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className='mt-4'>
            <label className='text-xs md:text-sm text-black/50 m-1'>{t('auth.username')}</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className='mt-2'>
            <label className='text-xs md:text-sm text-black/50 m-1'>{t('auth.password')}</label>
            <div className='relative w-full'>
              <Input id='password-input' type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} />
              <div onClick={() => {
                setShowPassword(!showPassword);
                document.getElementById('password-input')?.focus();
              }} className='text-xs underline cursor-pointer absolute top-1/2 right-4 -translate-y-1/2'>{showPassword ? t('general.hide') : t('general.show')}</div>
            </div>
          </div>
          <div className='mt-2'>
            <label className='text-xs md:text-sm text-black/50 m-1'>{t('auth.confirm-password')}</label>
            <div className='relative w-full'>
              <Input id='confirm-password-input' type={showConfirmPassword ? 'text' : 'password'} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
              <div onClick={() => {
                setShowConfirmPassword(!showConfirmPassword);
                document.getElementById('confirm-password-input')?.focus();
              }} className='text-xs underline cursor-pointer absolute top-1/2 right-4 -translate-y-1/2'>{showConfirmPassword ? t('general.hide') : t('general.show')}</div>
            </div>
          </div>
          {isCodeSent && <div className='mt-2'>
            <label className='text-xs md:text-sm text-black/50 m-1'>{t('auth.verification-code')}</label>
            <Input autoFocus value={verificationCode} onChange={(e) => setVerificationCode(e.target.value)} />
          </div>}
          <button onClick={submit} ref={submitRef} disabled={isLoading || !email || !name || password.length < 8 || confirmPassword !== password || (isCodeSent && !verificationCode)} className='border flex items-center justify-center bg-primary border-primary disabled:border-gray-300 disabled:bg-gray-300 text-white text-sm md:text-base w-full py-1.5 mt-8 rounded-lg'>{isLoading ? <Loading size={6} /> : (isCodeSent ? t('auth.sign-up') : t('auth.send-verification-code'))}</button>
          <div onClick={() => navigate('/signin')} className='cursor-pointer text-center text-sm md:text-base hover:text-primary mt-8 underline'>{t('auth.already-have-account')}</div>
        </div>
      </main>
      <Footer />
    </>
  )
}

export default SignUp